import { writable } from 'svelte/store';
import type { TagSummary } from '../types';

/**
 * Tag vocabulary store (plans/tags.md D6).
 *
 * The whole vocabulary is fetched once and cached in-memory; the tag picker
 * filters client-side as you type (single-user scale makes per-keystroke
 * server search unnecessary). Any tag mutation invalidates the cache and
 * triggers one background refetch.
 */

export const tags = writable<TagSummary[]>([]);

let fetchPromise: Promise<void> | null = null;

export async function loadTags(force = false): Promise<void> {
  if (fetchPromise && !force) return fetchPromise;

  fetchPromise = (async () => {
    try {
      const response = await fetch('/tags.json', { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const json = await response.json();
      tags.set(json.tags ?? []);
    } catch {
      // Keep the previous list on network failure; next mutation retries.
    } finally {
      fetchPromise = null;
    }
  })();

  return fetchPromise;
}

/** Called after any tag mutation (picker add/remove) to refresh counts/recency. */
export function invalidateTags(): void {
  loadTags(true);
}

/**
 * Filter the vocabulary client-side: prefix/substring match, case-insensitive,
 * excluding already-selected names. Empty query returns the full recent-first
 * list (the server orders by last_used_at desc).
 */
export function filterTags(all: TagSummary[], query: string, excludeNames: string[]): TagSummary[] {
  const excluded = new Set(excludeNames.map((n) => n.toLowerCase()));
  const q = query.trim().toLowerCase();
  return all.filter((t) => {
    if (excluded.has(t.name.toLowerCase())) return false;
    if (!q) return true;
    return t.name.toLowerCase().includes(q);
  });
}

/** Reset in tests / on sign-out. */
export function resetTags(): void {
  tags.set([]);
  fetchPromise = null;
}
